import { useMemo, useState, type KeyboardEvent } from "react";
import {
  categoryColor,
  formatCad,
  groupServiceVariants,
  serviceMatches,
  type CategoryWithServices,
  type Service,
  type ServiceVariantGroup,
  type VehicleClass,
} from "@shared/modules/x-atelier";
import { Icon } from "../../../core/components/ui/icon";
import { Input } from "../../../core/components/ui/input";
import { formatDuration } from "../lib/dates";
import { VariantStrip } from "./variant-chips";

/**
 * Choix des services d'un rendez-vous. Le catalogue importé décline un même
 * service en variantes (« Shampoing tapis — 1 tapis », « — 2 tapis »…) : les
 * lister à plat donnait dix entrées à lire une à une. Ici, le service apparaît
 * une fois et ses variantes sont des pastilles sous son nom. La recherche
 * traverse toutes les catégories, accents et casse ignorés.
 */

interface ServicePickerProps {
  categories: CategoryWithServices[];
  /** Identifiants des services déjà au rendez-vous. */
  selected: Set<string>;
  /** Choisir une variante : elle remplace celle du même groupe, ou se retire si déjà choisie. */
  onPick: (service: Service, group: ServiceVariantGroup) => void;
  /** Classe du véhicule : masque les variantes qui ne la concernent pas. */
  vehicleClass?: VehicleClass | null;
  /** Services les plus utilisés, proposés en tête. */
  frequent?: Service[];
  /** Retirer un service depuis le récapitulatif de la sélection. */
  onRemove?: (serviceId: string) => void;
  /**
   * Services déjà au rendez-vous quand on le modifie : leurs catégories passent
   * en tête, restent ouvertes même une fois le service retiré, et ces services
   * y viennent en premier — le remplacer ne demande aucune recherche.
   */
  priorityIds?: string[];
}

const OPEN_KEY = "atelier.servicePicker.ouvertes";

/** Catégories laissées ouvertes par l'employé, retrouvées au rendez-vous suivant. */
function readOpen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

function writeOpen(open: Set<string>) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify([...open]));
  } catch {
    // stockage indisponible : l'ouverture vaut pour ce rendez-vous seulement
  }
}

export function ServicePicker({
  categories,
  selected,
  onPick,
  vehicleClass,
  frequent = [],
  onRemove,
  priorityIds,
}: ServicePickerProps) {
  const [query, setQuery] = useState("");
  // Catégories repliées par défaut : tout déplier redonne le mur de services
  // qu'on essaie d'éviter. Restent ouvertes celles que l'employé a ouvertes et
  // celles qui contiennent un service choisi. Replier une catégorie ne retire
  // jamais ses services du rendez-vous : on ne fait que masquer leur liste.
  const [opened, setOpened] = useState<Set<string>>(readOpen);
  const [closedChosen, setClosedChosen] = useState<Set<string>>(() => new Set());

  const usable = useMemo(
    () =>
      categories
        .map((c) => ({
          ...c,
          services: c.services.filter(
            (s) =>
              s.active &&
              (!vehicleClass ||
                !s.vehicleClasses?.length ||
                s.vehicleClasses.includes(vehicleClass)),
          ),
        }))
        .filter((c) => c.services.length > 0),
    [categories, vehicleClass],
  );

  const searching = query.trim().length > 0;
  const results = useMemo(() => {
    if (!searching) return [];
    return usable.flatMap((c) =>
      groupServiceVariants(c.services.filter((s) => serviceMatches(s, query, c.name))).map((g) => ({
        group: g,
        category: c,
      })),
    );
  }, [usable, query, searching]);

  const frequentGroups = useMemo(() => groupServiceVariants(frequent), [frequent]);

  /** Services choisis par catégorie (pour l'indicateur et l'ouverture par défaut). */
  const chosenByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of categories)
      for (const s of c.services)
        if (selected.has(s.id)) counts.set(c.id, (counts.get(c.id) ?? 0) + 1);
    return counts;
  }, [categories, selected]);

  const chosenServices = useMemo(
    () =>
      categories.flatMap((c) =>
        c.services.filter((s) => selected.has(s.id)).map((s) => ({ s, c })),
      ),
    [categories, selected],
  );

  // Groupes (service et ses variantes) et catégories des services déjà au
  // rendez-vous. Figés à l'ouverture : cocher ou décocher ne déplace rien.
  const priority = useMemo(() => {
    const ids = new Set(priorityIds ?? []);
    const groups = new Set<string>();
    const cats = new Set<string>();
    if (ids.size)
      for (const c of categories)
        for (const g of groupServiceVariants(c.services))
          if (g.variants.some((v) => ids.has(v.id))) {
            groups.add(g.key);
            cats.add(c.id);
          }
    return { groups, cats };
  }, [categories, priorityIds]);

  const isOpen = (id: string) =>
    opened.has(id) ||
    (((chosenByCategory.get(id) ?? 0) > 0 || priority.cats.has(id)) && !closedChosen.has(id));

  const toggle = (id: string) => {
    const open = isOpen(id);
    setOpened((prev) => {
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      writeOpen(next);
      return next;
    });
    setClosedChosen((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const setAll = (open: boolean) => {
    const next = open ? new Set(usable.map((c) => c.id)) : new Set<string>();
    setOpened(next);
    writeOpen(next);
    setClosedChosen(open ? new Set() : new Set(usable.map((c) => c.id)));
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    // Entrée dans la recherche ne soumet pas le rendez-vous.
    if (e.key === "Enter") e.preventDefault();
  };

  const renderCategory = (c: CategoryWithServices) => {
    const all = groupServiceVariants(c.services);
    // Les services déjà au rendez-vous d'abord, puis l'ordre du catalogue.
    const groups = [
      ...all.filter((g) => priority.groups.has(g.key)),
      ...all.filter((g) => !priority.groups.has(g.key)),
    ];
    const open = isOpen(c.id);
    const chosen = chosenByCategory.get(c.id) ?? 0;
    return (
      <section key={c.id} className="service-picker__category">
        <button
          type="button"
          className="service-picker__category-name"
          aria-expanded={open}
          onClick={() => toggle(c.id)}
        >
          <span
            className="service-picker__category-dot"
            style={{ background: categoryColor(c) }}
            aria-hidden="true"
          />
          {c.name}
          <span className="service-picker__category-count" title="Services de la catégorie">
            {groups.length}
          </span>
          {chosen > 0 ? (
            <span className="service-picker__category-chosen">
              {chosen} sélectionné{chosen > 1 ? "s" : ""}
            </span>
          ) : null}
          <span className="service-picker__category-chevron" aria-hidden="true">
            <Icon name={open ? "chevron-up" : "chevron-down"} size={16} />
          </span>
        </button>
        {open ? (
          <div className="service-picker__rows">
            {groups.map((group) => (
              <ServiceRow
                key={group.key}
                group={group}
                color={categoryColor(c)}
                selected={selected}
                onPick={onPick}
                planned={priority.groups.has(group.key)}
              />
            ))}
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <div className="service-picker">
      <div className="service-picker__summary" role="status" aria-live="polite">
        <span className="service-picker__summary-count">
          {chosenServices.length === 0
            ? "Aucun service sélectionné"
            : `${chosenServices.length} service${chosenServices.length > 1 ? "s" : ""} sélectionné${chosenServices.length > 1 ? "s" : ""}`}
        </span>
        {chosenServices.length > 0 ? (
          <ul className="service-picker__chosen" aria-label="Services sélectionnés">
            {chosenServices.map(({ s, c }) => (
              <li key={s.id} className="service-picker__chosen-item">
                <span
                  className="service-picker__category-dot"
                  style={{ background: categoryColor(c) }}
                  aria-hidden="true"
                />
                {s.name}
                {onRemove ? (
                  <button
                    type="button"
                    className="service-picker__chosen-remove"
                    aria-label={`Retirer ${s.name}`}
                    onClick={() => onRemove(s.id)}
                  >
                    <Icon name="close" size={14} />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {!searching ? (
          <span className="service-picker__fold">
            <button type="button" className="service-picker__fold-btn" onClick={() => setAll(true)}>
              Tout déplier
            </button>
            <button
              type="button"
              className="service-picker__fold-btn"
              onClick={() => setAll(false)}
            >
              Tout replier
            </button>
          </span>
        ) : null}
      </div>

      <div className="service-picker__search">
        <span className="service-picker__search-icon" aria-hidden="true">
          <Icon name="search" size={16} />
        </span>
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKey}
          placeholder="Rechercher un service (ex : tapis, antirouille, nano…)"
          aria-label="Rechercher un service"
          autoComplete="off"
        />
      </div>

      {searching ? (
        results.length === 0 ? (
          <p className="service-picker__empty">Aucun service ne correspond à « {query} ».</p>
        ) : (
          <div className="service-picker__results">
            {results.map(({ group, category }) => (
              <ServiceRow
                key={group.key}
                group={group}
                categoryName={category.name}
                color={categoryColor(category)}
                selected={selected}
                onPick={onPick}
              />
            ))}
          </div>
        )
      ) : (
        <>
          {usable.filter((c) => priority.cats.has(c.id)).map(renderCategory)}

          {frequentGroups.length > 0 ? (
            <section className="service-picker__category">
              <h4 className="service-picker__category-name service-picker__category-name--plain">
                Les plus cédulés
              </h4>
              <div className="service-picker__rows">
                {frequentGroups.map((group) => (
                  <ServiceRow
                    key={`freq-${group.key}`}
                    group={group}
                    color={categoryColor(
                      usable.find((c) => c.id === group.categoryId) ?? { position: 0 },
                    )}
                    selected={selected}
                    onPick={onPick}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {usable.filter((c) => !priority.cats.has(c.id)).map(renderCategory)}
        </>
      )}
    </div>
  );
}

/**
 * Description lisible au comptoir : on retire les lignes qui ne sont qu'un
 * lien vers la procédure interne (le catalogue les garde, et la fiche du
 * travail donne le guide). Tout le reste du texte est montré en entier.
 */
function readable(description: string | null): string | null {
  if (!description) return null;
  const kept = description
    .split(/\r?\n/)
    .filter((l) => !/^\s*(?:(?:proc[ée]dure|lien)\s*:\s*)?https?:\/\/\S+\s*$/i.test(l))
    .join("\n")
    .trim();
  return kept || null;
}

/** Prix et durée d'une variante, en une ligne. */
function meta(s: Service): string {
  const price = s.quoteOnly || s.priceCents === null ? "Sur devis" : formatCad(s.priceCents);
  return `${price} · ${formatDuration(s.durationMin)}`;
}

/**
 * Un service : son nom, ce qu'il comprend (en entier — c'est ce qu'on lit au
 * comptoir pour répondre au client), puis ses variantes en pastilles.
 */
function ServiceRow({
  group,
  categoryName,
  color,
  selected,
  onPick,
  planned,
}: {
  group: ServiceVariantGroup;
  categoryName?: string;
  color: string;
  selected: Set<string>;
  onPick: (service: Service, group: ServiceVariantGroup) => void;
  /** Service déjà au rendez-vous à l'ouverture (repère « Au rendez-vous »). */
  planned?: boolean;
}) {
  const single = group.variants.length === 1 ? group.variants[0]! : null;
  const chosen = group.variants.find((v) => selected.has(v.id)) ?? null;
  // La description se répète d'une variante à l'autre : celle de la variante
  // choisie, sinon celle de la première qui en a une.
  const description = readable(
    (chosen ?? group.variants.find((v) => v.description) ?? group.variants[0])!.description,
  );
  const classes = [
    "service-picker__row",
    chosen && "is-chosen",
    single?.bundleServiceIds.length ? "service-picker__row--bundle" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} style={{ "--cat-color": color } as React.CSSProperties}>
      <div className="service-picker__head">
        {single ? (
          <label className="service-picker__single">
            <input
              type="checkbox"
              checked={selected.has(single.id)}
              onChange={() => onPick(single, group)}
            />
            <span className="service-picker__name">
              {group.name}
              {single.bundleServiceIds.length > 1 ? (
                <span className="service-picker__tag">forfait</span>
              ) : null}
            </span>
            <span className="service-picker__meta">{meta(single)}</span>
          </label>
        ) : (
          <>
            <span className="service-picker__name">{group.name}</span>
            <span className="service-picker__meta">{chosen ? meta(chosen) : null}</span>
          </>
        )}
        {planned ? <span className="service-picker__planned">Au rendez-vous</span> : null}
        {categoryName ? <span className="service-picker__category-tag">{categoryName}</span> : null}
      </div>

      {description ? <p className="service-picker__description">{description}</p> : null}

      {single ? null : (
        <div className="service-picker__variants">
          <VariantStrip
            variants={group.variants}
            ariaLabel={`${group.name} : choix`}
            selectedIds={selected}
            onPick={(v) => onPick(v, group)}
            withDuration={false}
          />
        </div>
      )}
    </div>
  );
}
